// next/link compat shim — wraps TanStack Router's real <Link>. Uses the same
// `href` escape hatch as navigation.tsx (untyped, fully-built URL string) so
// reused files pass arbitrary Next-style hrefs (with querystrings/hashes)
// without needing route-tree type knowledge here.
import { forwardRef, type AnchorHTMLAttributes, type ComponentProps, type ReactNode } from 'react';
import { Link as TanstackLink } from '@tanstack/react-router';

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string | { pathname?: string };
  children?: ReactNode;
  prefetch?: boolean;
  replace?: boolean;
  scroll?: boolean;
};

const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, prefetch, replace, scroll: _scroll, ...rest },
  ref,
) {
  const url = typeof href === 'string' ? href : (href?.pathname ?? '#');
  const linkProps = {
    ref,
    href: url,
    replace,
    // Next's default `prefetch` (viewport-based) has no exact TanStack
    // equivalent for an untyped href; map explicit `prefetch={false}` to
    // 'intent'-only (hover/focus), otherwise preload on viewport entry.
    preload: prefetch === false ? 'intent' : 'viewport',
    ...rest,
    // TanStack's <Link> types make `to` required unless the route tree
    // proves `href` alone resolves every path — it can't for an arbitrary
    // runtime string. This is the same intentional "opt out of typed
    // routing" tradeoff as navigation.tsx's redirect()/push(); burn this
    // cast down file-by-file as reused files migrate to typed `<Link to>`.
  } as unknown as ComponentProps<typeof TanstackLink>;
  return <TanstackLink {...linkProps} />;
});

export default Link;
